/**
 * Authentication Service
 * 
 * This service provides a clean abstraction layer for all authentication operations.
 * All Firebase Auth-specific code is encapsulated here.
 * 
 * Migration Strategy:
 * - When migrating to Azure AD B2C or another provider:
 *   1. Keep the same function signatures (inputs/outputs)
 *   2. Replace Firebase Auth calls with Azure/new provider equivalents
 *   3. Update import statements at the top
 *   4. Components using this service won't need changes
 * 
 * Example Azure Migration:
 * - Replace GoogleAuthProvider with Azure AD configuration
 * - Replace signInWithPopup with MSAL loginPopup
 * - Maintain the same return structure for user objects
 */

import {
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Initiate YouTube sign-in with redirect (avoids COOP popup issues)
 * @returns {Promise<void>} Redirects to Google OAuth
 * @throws {Error} Authentication error
 */
export const signInWithYouTube = async () => {
  try {
    const provider = new GoogleAuthProvider();

    // Add YouTube-specific scopes
    provider.addScope('https://www.googleapis.com/auth/youtube.readonly');
    provider.addScope('openid');
    provider.addScope('profile');
    provider.addScope('email');

    // Request access type for refresh token
    provider.setCustomParameters({
      access_type: 'offline',
      prompt: 'consent'
    });

    // Redirect to Google OAuth instead of popup
    await signInWithRedirect(auth, provider);
  } catch (error) {
    console.error('YouTube sign-in redirect error:', error);
    throw new Error(error.message || 'Failed to initiate YouTube sign-in');
  }
};

/**
 * Get the result of a redirect sign-in
 * @returns {Promise<Object|null>} User object with credentials or null
 * @throws {Error} Authentication error
 */
export const getYouTubeSignInResult = async () => {
  try {
    const result = await getRedirectResult(auth);

    if (!result) {
      return null; // No redirect result (user not returning from OAuth)
    }

    // Extract user info and access token
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken;

    return {
      user: result.user,
      accessToken,
      credential,
    };
  } catch (error) {
    console.error('YouTube sign-in result error:', error);
    throw new Error(error.message || 'Failed to complete YouTube sign-in');
  }
};

/**
 * Fetch YouTube channel information using access token
 * @param {string} accessToken - OAuth access token
 * @returns {Promise<Object>} Channel information
 */
export const fetchYouTubeChannelInfo = async (accessToken) => {
  try {
    const response = await fetch(
      'https://www.googleapis.com/youtube/v3/channels?part=snippet&mine=true',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch YouTube channel info');
    }

    const data = await response.json();
    
    if (!data.items || data.items.length === 0) {
      throw new Error('No YouTube channel found for this account');
    }

    const channel = data.items[0];
    
    return {
      channelId: channel.id,
      channelTitle: channel.snippet.title,
      thumbnailUrl: channel.snippet.thumbnails.default.url,
      description: channel.snippet.description,
    };
  } catch (error) {
    console.error('Error fetching YouTube channel:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw new Error('Failed to sign out');
  }
};

/**
 * Get the current authenticated user
 * @returns {Object|null} Current user or null
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Subscribe to authentication state changes
 * @param {Function} callback - Called when auth state changes
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAuthState = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const isAuthenticated = () => {
  return !!auth.currentUser;
};

/**
 * Get current user's ID token
 * @returns {Promise<string>} ID token
 */
export const getIdToken = async () => {
  const user = getCurrentUser();
  if (!user) {
    throw new Error('No authenticated user');
  }
  return await user.getIdToken();
};
